import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useVerifyCode, useResendCode } from '../../../helpers/api/users/users';

/**
 * Public landing for users verifying via a WhatsApp code. They enter their email
 * and the 6-digit code; on success we exchange it for a password-setup token and
 * forward to the existing set-up-account (password) screen.
 */
const VerifyCode: React.FC = () => {
  const { t } = useTranslation('AccountPage');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const verify = useVerifyCode(
    (res: any) => {
      const body = res?.data ?? res;
      const token = body?.data?.token ?? body?.token;
      if (token) {
        navigate(`/set-up-account?token=${encodeURIComponent(token)}`);
      } else {
        setError(t('verifyCode.noToken', 'Could not verify the code. Please try again.'));
      }
    },
    (res: any, err: any) => {
      const body = res?.data ?? res;
      setError(body?.message || err?.message || t('verifyCode.invalid', 'Invalid or expired code.'));
    }
  );

  const resend = useResendCode(
    () => setError(t('verifyCode.resent', 'A new code has been sent.')),
    (res: any, err: any) => {
      const body = res?.data ?? res;
      setError(body?.message || err?.message || t('verifyCode.resendFailed', 'Could not resend the code.'));
    }
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || code.trim().length < 4) {
      setError(t('verifyCode.fillFields', 'Enter your email and the code.'));
      return;
    }
    verify.mutate({ email: email.trim(), code: code.trim() });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('verifyCode.title', 'Verify your account')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('verifyCode.subtitle', 'Enter the code we sent to your WhatsApp.')}
        </p>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">
              {t('verifyCode.email', 'Email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-slate-200 dark:border-gray-700 p-2.5 bg-transparent dark:bg-gray-900 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">
              {t('verifyCode.code', 'Verification code')}
            </label>
            <input
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="••••••"
              required
              className="mt-1 w-full tracking-[0.5em] text-center text-lg rounded-lg border border-slate-200 dark:border-gray-700 p-2.5 bg-transparent dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={verify.isPending}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2.5 text-sm font-medium"
          >
            {verify.isPending ? t('verifyCode.verifying', 'Verifying…') : t('verifyCode.verify', 'Verify')}
          </button>
        </form>

        <button
          type="button"
          onClick={() => email && resend.mutate({ email: email.trim() })}
          disabled={!email || resend.isPending}
          className="mt-4 w-full text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
        >
          {t('verifyCode.resend', 'Resend code')}
        </button>
      </div>
    </div>
  );
};

export default VerifyCode;
